import { ReadinessGaugeInstance } from '@esg/esg-global-types';
import React from 'react';
import { Autocomplete, AutocompleteRenderInputParams, Box, TextField } from '@mui/material';
import {
  getReadinessGaugeInstances,
  readiness_gauge_instance_label
} from '../../../../lib/readiness_gauge/readiness_gauge_instance';

interface ReadinessGaugeInstanceSelectProps {
  handleChangeReadinessInstances?: (
    modules: Array<ReadinessGaugeInstance> | ReadinessGaugeInstance | null
  ) => void;
  options?: Array<ReadinessGaugeInstance>;
  selected_options?: Array<ReadinessGaugeInstance> | ReadinessGaugeInstance | null;
  disabled?: boolean;
  input_label?: string;
  multi_select?: boolean;
  tag_limit?: number;
  clearable?: boolean;
  searchable?: boolean;
  group_id?: string;
  loading?: boolean;
  color?: string;
  auto_select?: boolean;
}

/**
 * ReadinessGaugeInstance selection auto-complete component that handles a variety of built-in scenarios.
 * @param {Array<ReadinessGaugeInstance> | ReadinessGaugeInstance | null} props.handleChangeReadinessInstances Callback function to notify the parent component when the selected options have changed.
 * @param {Array<ReadinessGaugeInstance>} props.options Custom list of options provided by the parent component. Defaults to none.
 * @param {Array<ReadinessGaugeInstance> | ReadinessGaugeInstance | null} props.selected_options Custom list of selected options provided by the parent component. Changes component from an uncontrolled to controlled.
 * @param {boolean} props.disabled Renders the component in a disabled state along with any other provided components. Defaults to enabled.
 * @param {string} props.input_label Sets a custom component input label. Defaults to the singular version of the main entity name.
 * @param {boolean} props.multi_select Allow multi option selection and keeps the select open while selecting. Defaults to a single select and closes after selecting an option.
 * @param {number} props.tag_limit Sets a limit on the amount of tags rendered in the input. Defaults to unlimited.
 * @param {boolean} props.clearable Allows the autocomplete to be cleared. Defaults to true.
 * @param {boolean} props.searchable Allows the user to type to search in the autocomplete. Defaults to true.
 * @param {boolean} props.loading A flag to set the autocomplete into a loading state for things such as fetching entries from database.
 * @param {boolean} props.auto_select Pre-populate the autocomplete input when only one division is available.
 * @param {string} props.group_id Group for which options will be queried.
 */

const ReadinessGaugeInstanceSelect = (props: ReadinessGaugeInstanceSelectProps) => {
  const [optionList, setOptions] = React.useState<Array<ReadinessGaugeInstance>>([]);
  const [selectedOptions, setSelectedOptions] = React.useState<
    Array<ReadinessGaugeInstance> | ReadinessGaugeInstance | null
  >(props.multi_select ? [] : null);

  // Define autocomplete options
  React.useEffect(() => {
    (async () => {
      const options: Array<ReadinessGaugeInstance> = props.options
        ? // Readiness Gauge Instances supplied by the parent component.
          props.options
        : props.group_id
          ? await getReadinessGaugeInstances(props.group_id)
          : [];
      setOptions(options);
    })();
  }, [props.options]);

  // Define selected options
  React.useEffect(() => {
    setSelectedOptions(
      props.multi_select
        ? Array.isArray(props.selected_options)
          ? props.selected_options
          : []
        : props.selected_options
          ? props.selected_options
          : null
    );
  }, [props.options, props.selected_options]);

  return (
    <Autocomplete
      id="readiness-gauge-instance-select"
      fullWidth
      disabled={props.disabled}
      loading={props.loading}
      limitTags={props.tag_limit}
      disableClearable={props.clearable === false}
      options={optionList}
      value={selectedOptions}
      getOptionLabel={(option: ReadinessGaugeInstance) => {
        return option.name;
      }}
      renderOption={(render_props, option) => (
        <Box
          component="li"
          sx={{ display: 'flex', alignItems: 'center' }}
          {...render_props}
          key={option.id}
        >
          {option.name}
        </Box>
      )}
      disableCloseOnSelect={props.multi_select}
      isOptionEqualToValue={(option: ReadinessGaugeInstance, value: ReadinessGaugeInstance) =>
        option.id === value.id && option.id === value.id
      }
      multiple={props.multi_select}
      noOptionsText={`No ${readiness_gauge_instance_label.many.toLowerCase()}`}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField
          {...params}
          label={props.input_label ?? readiness_gauge_instance_label.one}
          variant="standard"
        />
      )}
      onChange={(
        event: React.SyntheticEvent<Element, Event>,
        value: Array<ReadinessGaugeInstance> | ReadinessGaugeInstance | null
      ) => {
        setSelectedOptions(value);
        props.handleChangeReadinessInstances && props.handleChangeReadinessInstances(value);
      }}
    />
  );
};

export default ReadinessGaugeInstanceSelect;
